'use client';

import React from 'react';
import { TextHeadline, TextHeadlineProps } from '../TextHeadline/TextHeadline';
import { TextParagraph, TextParagraphProps } from '../TextParagraph/TextParagraph';

/**
 * Base props shared between both text types
 */
interface BaseTextContentProps {
  align?: TextParagraphProps['align'];
  /** Additional CSS classes */
  className?: string;
  /** Child content */
  children?: React.ReactNode;
  /** HTML data-content attribute */
  dataContent?: string;
  /** HTML id attribute */
  id?: string;
  /** ARIA label for accessibility */
  'aria-label'?: string;
  /** ARIA describedby for accessibility */
  'aria-describedby'?: string;
  /** Optional label for the text content (instead of children) */
  label?: string;
  /** Test ID for testing purposes */
  testId?: string;
  /** Optional state to indicate content is being loaded */
  isLoading?: boolean;
  /** Additional CSS classes to apply when loading */
  loadingClassName?: string;
  /** Number of loading lines to display */
  loadingLines?: number;
}

/**
 * Props for headline content type
 */
interface HeadlineContentProps extends BaseTextContentProps {
  /** Content type discriminator */
  type: 'headline';
  /** Headline-specific props (excluding shared props) */
  level: TextHeadlineProps['level'];
  variant?: TextHeadlineProps['variant'];
  color?: TextHeadlineProps['color'];
}

/**
 * Props for paragraph content type
 */
interface ParagraphContentProps extends BaseTextContentProps {
  /** Content type discriminator */
  type: 'paragraph';
  /** Paragraph-specific props (excluding shared props) */
  as?: TextParagraphProps['as'];
  variant?: TextParagraphProps['variant'];
  color?: TextParagraphProps['color'];
  weight?: TextParagraphProps['weight'];
}

/**
 * Union type for all possible props
 */
export type TextContentProps = HeadlineContentProps | ParagraphContentProps;

/**
 * TextContent Component
 *
 * A unified text component that can render either headlines or paragraphs
 * based on the `type` prop. This provides a single interface for all text
 * rendering while maintaining full access to the underlying component features.
 *
 * Features:
 * - Discriminated union for type safety
 * - Full access to underlying component props
 * - Consistent API across text types
 * - TypeScript autocompletion for appropriate props
 *
 * @example
 * ```tsx
 * // Render a headline
 * <TextContent
 *   type="headline"
 *   level={1}
 *   variant="pageTitle"
 *   color="primary"
 * >
 *   Page Title
 * </TextContent>
 *
 * // Render a paragraph
 * <TextContent
 *   type="paragraph"
 *   variant="bodyLarge"
 *   color="secondary"
 *   weight="medium"
 * >
 *   This is a paragraph with medium weight.
 * </TextContent>
 *
 * // Render a span element
 * <TextContent
 *   type="paragraph"
 *   as="span"
 *   variant="label"
 *   align="center"
 * >
 *   Centered label text
 * </TextContent>
 * ```
 */
export const TextContent: React.FC<TextContentProps> = (props) => {
  const {
    type,
    align = 'inherit',
    className,
    loadingClassName,
    loadingLines = 1,
    children,
    label,
    id,
    isLoading,
    testId,
    dataContent,
    'aria-label': ariaLabel,
    'aria-describedby': ariaDescribedBy,
  } = props;

  const content = label ?? children;

  // Shared props for both components
  const sharedProps = {
    align,
    className,
    children: content,
    id,
    isLoading,
    loadingClassName,
    loadingLines,
    'aria-describedby': ariaDescribedBy,
    'aria-label': ariaLabel,
    'data-content': dataContent,
    'data-testid': testId,
  };

  if (type === 'headline') {
    const { level, variant, color } = props;
    return (
      <TextHeadline
        level={level}
        variant={variant}
        color={color}
        {...sharedProps}
      />
    );
  }

  if (type === 'paragraph') {
    const { as, variant, color, weight } = props;
    return (
      <TextParagraph
        as={as}
        variant={variant}
        color={color}
        weight={weight}
        {...sharedProps}
      />
    );
  }

  // TypeScript should prevent this, but added for runtime safety
  throw new Error(`Invalid TextContent type: ${type}`);
}

/**
 * Convenience components for specific use cases
 */

/** Render headline content */
export const TextContentHeadline = (props: Omit<HeadlineContentProps, 'type'>) => (
  <TextContent type="headline" {...props} />
);

/** Render paragraph content */
export const TextContentParagraph = (props: Omit<ParagraphContentProps, 'type'>) => (
  <TextContent type="paragraph" {...props} />
);

// Export the main component as default
TextContent.displayName = 'TextContent';

export default TextContent;
